/**
 * SignupPage - User registration with Google Sign-in and email/password
 */

import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { AuthLayout } from '../../layouts';
import { AuthBrandingHeader } from './AuthBrandingHeader';
import { checkAccessStatus } from '../../api/access';

export const SignupPage = () => {
  const navigate = useNavigate();
  const { signInWithGoogle, signUpWithEmail } = useAuth();
  const isBetaEnabled = useFeatureFlag('BETA');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);

  // Redirect to beta-access if BETA is enabled and user navigates directly
  useEffect(() => {
    if (isBetaEnabled) {
      navigate('/beta-access', { replace: true });
    }
  }, [isBetaEnabled, navigate]);

  const checkBetaAccess = async (emailToCheck: string): Promise<boolean> => {
    if (!isBetaEnabled) {
      return true; // Bypass check if BETA is OFF
    }

    try {
      setCheckingAccess(true);
      const { status } = await checkAccessStatus(emailToCheck);

      if (status !== 'allowed') {
        // Redirect to beta-access page with email pre-filled
        navigate(`/beta-access?email=${encodeURIComponent(emailToCheck)}`, { replace: true });
        return false;
      }

      return true;
    } catch (err) {
      console.error('Failed to check beta access:', err);
      setError('Failed to verify beta access. Please try again.');
      return false;
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);

      // Note: Google sign-in doesn't provide email upfront, so we can't check access
      // The backend will handle enforcement in Packet 7
      await signInWithGoogle();
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (e: FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    if (password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    // Check beta access before proceeding
    const hasAccess = await checkBetaAccess(email);
    if (!hasAccess) {
      return; // checkBetaAccess already redirected
    }

    try {
      setError('');
      setLoading(true);
      await signUpWithEmail(email, password, name);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.25),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(59,130,246,0.25),transparent_30%),radial-gradient(circle_at_50%_80%,rgba(14,165,233,0.2),transparent_30%)]" />

        <div className="relative max-w-6xl mx-auto px-6 py-16">
          <div className="flex items-center justify-between mb-10">
            <AuthBrandingHeader eyebrow="New to Smart Prompt" headline="Create your workspace" />
            <div className="hidden md:flex items-center gap-2 text-sm text-slate-400 bg-white/5 border border-slate-800 px-4 py-2 rounded-full">
              <span className="inline-flex h-2 w-2 rounded-full bg-cyan-400 animate-pulse" aria-hidden="true" />
              Guided onboarding — no credit card
            </div>
          </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-white/5 border border-slate-800 rounded-2xl p-8 shadow-2xl backdrop-blur-xl flex flex-col justify-between">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-400/10 text-cyan-200 text-xs font-semibold border border-cyan-400/30">
                <span className="inline-flex h-2 w-2 rounded-full bg-cyan-400" aria-hidden="true" />
                14-day full features
              </div>
              <h1 className="text-4xl font-bold leading-tight">Start a secure workspace in minutes</h1>
              <p className="text-lg text-slate-300">
                Glassmorphism layout, gradient accents, and the same hierarchy as the sign-in page so your team sees a consistent brand.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 pt-2">
                {["Branded Google sign-in", "Email fallback with guardrails", "Mobile responsive panels", "Audit-ready auth events"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-slate-200 bg-slate-900/40 border border-slate-800 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between gap-3 text-sm text-slate-300 bg-slate-900/40 border border-slate-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-slate-950 font-semibold shadow-lg shadow-emerald-500/20">
                  <span>ISO</span>
                </div>
                <div>
                  <p className="font-semibold text-white">Security-first enrollment</p>
                  <p className="text-slate-400">Encrypted auth tokens, password policy checks, and session alerts.</p>
                </div>
              </div>
              <Link to="/login" className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-slate-700 text-white transition-colors">
                <span>Already set up?</span>
                <span className="text-cyan-300 font-semibold">Sign in</span>
              </Link>
            </div>
          </div>

          <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-8 shadow-2xl backdrop-blur-2xl">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold text-slate-200 border border-white/10 mb-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" aria-hidden="true" />
                  Trusted providers
                </div>
                <h2 className="text-2xl font-semibold">Create account</h2>
                <p className="text-sm text-slate-400">Keep your credentials safe with mirrored login styling.</p>
              </div>
              <Link to="/login" className="text-sm text-cyan-300 hover:text-cyan-200 font-semibold">
                Sign in
              </Link>
            </div>

            {error && (
              <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-100 px-4 py-3 mb-4">
                <div className="text-sm font-medium">{error}</div>
              </div>
            )}

            <div className="space-y-6">
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-slate-800 bg-white text-slate-900 font-semibold shadow-lg shadow-slate-900/30 hover:-translate-y-0.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true" role="img">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.2 3.4-4.5 6-8.3 6-5 0-9-4-9-9s4-9 9-9c2.3 0 4.4.9 6 2.3l5.7-5.7C35.5 10.1 30.1 8 24 8c-8.8 0-16 7.2-16 16s7.2 16 16 16c8 0 15.4-5.8 16-14 0-.9.1-1.7.1-2.5z" />
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.3 16 18.8 12 24 12c2.3 0 4.4.9 6 2.3l5.7-5.7C32.9 5.1 28.1 3 22.7 3 14.8 3 7.8 7.6 4.2 14.1z" />
                  <path fill="#4CAF50" d="M24 44c5.8 0 11.1-2.2 15.1-5.9l-7-5.9c-2.1 1.4-4.7 2.2-7.4 2.2-5.2 0-9.7-3.3-11.5-8l-7.1 5.5C8.9 39.6 15.9 44 24 44z" />
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3C34.7 31.6 30.7 35 24 35c-5.2 0-9.5-3.3-11.3-8l-7.1 5.5C8.9 39.6 15.9 44 24 44c8.1 0 15-4.6 18.4-11.3 1.2-2.4 1.8-5 1.8-7.7 0-1.3-.1-2.7-.6-4z" />
                </svg>
                Continue with Google
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-slate-800" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-slate-950/60 text-slate-400">Or sign up with email</span>
                </div>
              </div>

              <form onSubmit={handleEmailSignUp} className="space-y-5">
                <div className="space-y-2">
                  <label htmlFor="name" className="block text-sm font-medium text-slate-200">
                    Full name
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 block w-full px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-slate-200">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 block w-full px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-200">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 block w-full px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-200">
                    Confirm password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 block w-full px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || checkingAccess}
                  className="w-full flex justify-center items-center gap-2 py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500 shadow-lg shadow-cyan-500/20 hover:from-cyan-400 hover:to-blue-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-slate-950 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkingAccess ? 'Checking access…' : loading ? 'Creating account…' : 'Create account'}
                </button>
              </form>

              <p className="text-center text-sm text-slate-400">
                Already have an account?{' '}
                <Link to="/login" className="font-semibold text-cyan-300 hover:text-cyan-200">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </AuthLayout>
  );
};
